import { IconButton, Tooltip } from '@mui/material';
import DarkModeOutlined from '@mui/icons-material/DarkModeOutlined';
import LightModeOutlined from '@mui/icons-material/LightModeOutlined';
import { useThemeMode } from './ThemeModeProvider';

interface ThemeToggleProps {
  /** Tamano del icono. `small` para barras compactas. */
  size?: 'small' | 'medium';
  className?: string;
}

/**
 * Interruptor de tema claro/oscuro.
 *
 * Detalles de accesibilidad que no son opcionales:
 *
 *   - `aria-label` describe la ACCION (a que tema se cambia), no el estado
 *     actual. Un lector de pantalla que anuncie solo el icono no comunica nada.
 *   - `aria-pressed` expone el estado como un control de dos posiciones, para
 *     que se pueda consultar sin depender del color del icono.
 *   - El tooltip duplica la etiqueta de forma visual, pero no la sustituye.
 */
export const ThemeToggle = ({ size = 'medium', className }: ThemeToggleProps) => {
  const { mode, toggle } = useThemeMode();
  const goingTo = mode === 'dark' ? 'claro' : 'oscuro';
  const label = `Cambiar a tema ${goingTo}`;

  return (
    <Tooltip title={label}>
      <IconButton
        onClick={toggle}
        size={size}
        className={className}
        aria-label={label}
        aria-pressed={mode === 'light'}
        sx={{
          color: 'text.secondary',
          border: '1px solid',
          borderColor: 'divider',
          '&:hover': {
            color: 'primary.main',
            borderColor: 'primary.main',
            backgroundColor: 'action.hover',
          },
        }}
      >
        {mode === 'dark' ? (
          <LightModeOutlined fontSize={size === 'small' ? 'small' : 'medium'} />
        ) : (
          <DarkModeOutlined fontSize={size === 'small' ? 'small' : 'medium'} />
        )}
      </IconButton>
    </Tooltip>
  );
};

export default ThemeToggle;
